// v1.2.0 - 2026-02-02 - React hooks for API calls - Fixed infinite loading when disabled

import { useState, useCallback, useEffect, useRef } from 'react'
import { ApiError } from '@/lib/api/client'

interface UseQueryResult<T> {
  data: T | null
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
}

interface UseMutationResult<T, V> {
  mutate: (variables: V) => Promise<T>
  data: T | null
  isLoading: boolean
  error: string | null
  reset: () => void
}

export function useQuery<T>(
  queryFn: () => Promise<T>,
  options?: { enabled?: boolean; refetchOnMount?: boolean }
): UseQueryResult<T> {
  const enabled = options?.enabled ?? true

  const [data, setData] = useState<T | null>(null)
  // Only show loading if enabled - prevents infinite spinner when waiting for deps
  const [isLoading, setIsLoading] = useState(enabled)
  const [error, setError] = useState<string | null>(null)

  const refetch = useCallback(async () => {
    if (!enabled) return

    setIsLoading(true)
    setError(null)

    try {
      const result = await queryFn()
      setData(result)
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.errorMessage)
      } else {
        setError('Errore di connessione')
      }
    } finally {
      setIsLoading(false)
    }
  }, [queryFn, enabled])

  // Track queryFn identity to refetch when it changes
  const queryFnRef = useRef(queryFn)

  // Handle enabled state changes
  useEffect(() => {
    if (enabled) {
      refetch()
    } else {
      // Reset loading state when disabled to prevent infinite spinner
      setIsLoading(false)
    }
  }, [enabled, refetch])

  // Refetch when queryFn changes (filter changes)
  useEffect(() => {
    if (queryFnRef.current !== queryFn) {
      queryFnRef.current = queryFn
      if (enabled) {
        refetch()
      }
    }
  }, [queryFn, enabled, refetch])

  return { data, isLoading, error, refetch }
}

export function useMutation<T, V = void>(
  mutationFn: (variables: V) => Promise<T>
): UseMutationResult<T, V> {
  const [data, setData] = useState<T | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const mutate = useCallback(
    async (variables: V) => {
      setIsLoading(true)
      setError(null)

      try {
        const result = await mutationFn(variables)
        setData(result)
        return result
      } catch (e) {
        const errorMessage = e instanceof ApiError ? e.errorMessage : 'Errore di connessione'
        setError(errorMessage)
        throw e
      } finally {
        setIsLoading(false)
      }
    },
    [mutationFn]
  )

  const reset = useCallback(() => {
    setData(null)
    setError(null)
    setIsLoading(false)
  }, [])

  return { mutate, data, isLoading, error, reset }
}
